/* eslint-disable complexity */
'use client';

import Image from 'next/image';
import { useState } from 'react';

import { BubbleAvatar } from '../common/bubble-avatar.ui';
import { MessageSeenIndicator } from './message-seen-indicator.ui';

import { ImageData, Message } from '@/slw/entities/chat';
import { convertDate } from '@/slw/shared/lib/date';
import { formatCurrency } from '@/slw/shared/lib/format-number';
import { cn } from '@/slw/shared/lib/utils';
import { ChatBubble, ChatBubbleMessage, ChatBubbleTimestamp } from '@/slw/widgets/chat';

// import { MessageActions } from './message-actions';
import { useAuthStore } from '@/slw/shared/stores';

interface MessageItemProps {
  message: Message;
  variant: 'sent' | 'received';
  index: number;
  onActionClick?: (actionType: string, messageIndex: number) => void;
  openModal: (imgUrl: ImageData) => void;
  chatId: string | null;
  isLastMessage: boolean;
}

export function MessageItem({ message, variant, index, openModal, chatId, isLastMessage }: MessageItemProps) {
  // const handleActionClick = (actionType: string) => {
  //   if (onActionClick) {
  //     onActionClick(actionType, index);
  //   }
  // };
  const [imgUrl, setUrlImg] = useState<string>(message?.listingData?.imageUrl || '');

  const { user: currentUser } = useAuthStore();

  return (
    <div key={index} id={message.id} className='flex flex-col gap-2 p-2'>
      <ChatBubble variant={variant} className={message.listingData ? 'max-w-full w-full' : ''}>
        {!message.listingData && <BubbleAvatar src={message.avatar} name={message.senderName} />}
        {message.listingData ? (
          <div className='w-full '>
            <div className='bg-[#F8F8F7] rounded-sm flex items-center p-2 overflow-hidden'>
              <div className='flex p-3 gap-3'>
                <div className='relative w-[86px] h-[64px] bg-gray-100 rounded-md overflow-hidden flex-shrink-0'>
                  {message.listingData.imageUrl ? (
                    <Image
                      src={imgUrl}
                      alt={message.listingData.title}
                      className='object-cover object-center hover:scale-105 transition-transform duration-200'
                      fill
                      sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 86px'
                      onError={() => setUrlImg('/images/common/no-image-available.png')}
                    />
                  ) : (
                    <div className='flex items-center justify-center h-full text-gray-400'>No Image</div>
                  )}
                </div>
                <div className='flex flex-col justify-between py-0.5 gap-1'>
                  <h3 className='text-xs font-normal leading-tight truncate text-ellipsis line-clamp-1'>
                    {message.listingData.title}
                  </h3>
                  <p className='ext-xs font-normal '>{message.listingData.listingId}</p>
                  <p className='text-xs font-normal'>{formatCurrency(message.listingData?.price || null)}</p>
                </div>
              </div>
              <a
                target='_blank'
                href={`/listing/${message.listingData.url}`}
                className='border ml-auto h-fit border-[#BA9C64] text-[#BA9C64] 
                py-2 px-4 rounded-md text-center text-sx 
                  hover:bg-[#BA9C64]/10'
              >
                View details
              </a>
            </div>
          </div>
        ) : message.imgUrls && message.imgUrls.length > 0 ? (
          <div>
            {message.text && (
              <ChatBubbleMessage
                variant={variant}
                isLoading={message.isLoading}
                className={cn('w-fit', variant === 'sent' ? 'ml-auto' : 'mr-auto')}
              >
                {message.text}
              </ChatBubbleMessage>
            )}
            <div
              className={cn(
                'grid gap-2 mt-2',
                message.imgUrls?.length === 1
                  ? 'grid-cols-1'
                  : message.imgUrls?.length === 2
                    ? 'grid-cols-2'
                    : message.imgUrls?.length === 4
                      ? 'grid-cols-2 grid-rows-2'
                      : message.imgUrls?.length >= 5 && message.imgUrls?.length <= 6
                        ? 'grid-cols-3 grid-rows-2'
                        : message.imgUrls?.length >= 7 && message.imgUrls?.length <= 9
                          ? 'grid-cols-3 grid-rows-3'
                          : 'grid-cols-3',
                'max-w-fit',
              )}
            >
              {message.imgUrls?.slice(0, 9).map((imgUrl, imgIndex) =>
                imgIndex === 8 && message?.imgUrls && message?.imgUrls?.length > 9 ? (
                  <button
                    key={imgIndex}
                    type='button'
                    className={cn(
                      'relative aspect-square overflow-hidden rounded-md cursor-pointer border-0 p-0',
                      'w-[5rem] h-[5rem]',
                    )}
                    onClick={() => {
                      openModal({
                        src: imgUrl,
                        alt: `Image ${imgIndex + 1}`,
                        allImages: message.imgUrls,
                        currentIndex: imgIndex,
                      });
                    }}
                    aria-label={`View image ${imgIndex + 1} and 
                    ${message.imgUrls?.length ? message.imgUrls.length - 9 : 0} more`}
                  >
                    <Image
                      src={imgUrl}
                      alt={`Image ${imgIndex + 1}`}
                      fill
                      sizes='(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 5rem'
                      className='object-cover w-full h-full hover:scale-105 transition-transform opacity-70'
                    />
                    <div
                      className='absolute inset-0 flex items-center justify-center
                     bg-black bg-opacity-50 text-white font-medium'
                    >
                      +{message.imgUrls?.length ? message.imgUrls.length - 9 : 0}
                    </div>
                  </button>
                ) : (
                  <button
                    type='button'
                    key={imgIndex}
                    className={cn(
                      'relative aspect-square overflow-hidden rounded-md border-0 p-0',
                      message.imgUrls?.length === 1 ? 'w-[12rem] h-[12rem]' : 'w-[5rem] h-[5rem]',
                    )}
                    onClick={() => {
                      openModal({
                        src: imgUrl,
                        alt: `Image ${imgIndex + 1}`,
                        allImages: message.imgUrls,
                        currentIndex: imgIndex,
                      });
                    }}
                    aria-label={`View image ${imgIndex + 1}`}
                  >
                    <Image
                      src={imgUrl}
                      alt={`Image ${imgIndex + 1}`}
                      fill
                      sizes='(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 5rem'
                      className='object-cover w-full h-full hover:scale-105 transition-transform'
                    />
                  </button>
                ),
              )}
            </div>
            <div
              className={cn(
                variant !== 'received' ? 'text-right justify-end' : 'text-left justify-start',
                'w-full text-gray-500 text-xs inline-flex gap-2',
              )}
            >
              {convertDate(message.createdAt.toDate())}
              {isLastMessage && (
                <MessageSeenIndicator
                  className=''
                  message={message}
                  chatId={chatId}
                  currentUserId={currentUser.id || ''}
                />
              )}
            </div>
          </div>
        ) : (
          <div className='relative'>
            <ChatBubbleMessage variant={variant} isLoading={message.isLoading}>
              {message.text}
              {message.createdAt && (
                <ChatBubbleTimestamp
                  // className='absolute bottom-0 right-0 top-full'
                  timestamp={convertDate(message.createdAt.toDate())}
                />
              )}
            </ChatBubbleMessage>

            {isLastMessage && (
              <MessageSeenIndicator
                className='absolute right-0'
                message={message}
                chatId={chatId}
                currentUserId={currentUser.id || ''}
              />
            )}
          </div>
        )}
        {/* <MessageActions onActionClick={handleActionClick} /> */}
      </ChatBubble>
    </div>
  );
}
